import React, { useRef, useState } from 'react';
import { ArrowLeft, Pencil, Plug, ChevronDown, Check, Save, Loader2, Cpu } from 'lucide-react';
import { WS } from './workspaceData.ts';

const PREDEFINED_ICONS = [
  '🤖','🧠','🔍','📊','💼','✉️',
  '📅','🌐','🔧','💡','🎯','📝',
  '🚀','⚡','🔒','📱','💻','🎨',
  '🔬','🌍','🧬','🤝','📈','🏆',
  '🔮','🌟','🦾','🎭','🧩','📣',
];

interface WorkspaceTopBarProps {
  isNew: boolean;
  name: string;
  icon: string;
  setIcon: (v: string) => void;
  prefill?: any;
  enabled: boolean;
  saving: boolean;
  saved: boolean;
  canSave?: boolean;
  onSave: () => void;
  onBack: () => void;
}

export default function WorkspaceTopBar({
  isNew, name, icon, setIcon,
  prefill, enabled,
  saving, saved, onSave, onBack,
  canSave = true,
}: WorkspaceTopBarProps) {
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [iconHovered, setIconHovered] = useState(false);
  const iconPickerRef = useRef<HTMLDivElement>(null);
  const iconFileRef   = useRef<HTMLInputElement>(null);

  // Close picker on outside click
  React.useEffect(() => {
    if (!iconPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (iconPickerRef.current && !iconPickerRef.current.contains(e.target as Node))
        setIconPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [iconPickerOpen]);

  const handleIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) { alert('Image too large (max 500 KB)'); return; }
    const reader = new FileReader();
    reader.onload = () => { setIcon(reader.result as string); setIconPickerOpen(false); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div className="ws-topbar" style={{
      flexShrink: 0,
      background: WS.surface,
      borderBottom: `1px solid ${WS.border}`,
      display: 'flex',
      alignItems: 'center',
      padding: '0 18px',
      gap: 12,
      boxShadow: '0 1px 0 rgba(17,24,39,0.05)',
    }}>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 8,
          border: `1px solid ${WS.border}`, background: 'transparent',
          color: WS.ink3, fontSize: 12, fontWeight: 500,
          cursor: 'pointer', fontFamily: 'inherit',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = WS.surface2; e.currentTarget.style.color = WS.ink; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = WS.ink3; }}
      >
        <ArrowLeft size={13} />
        Agents
      </button>

      {/* Divider */}
      <div className="ws-topbar-divider" style={{ width: 1, height: 20, background: WS.border }} />

      {/* Icon Picker */}
      <div ref={iconPickerRef} style={{ position: 'relative', flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => setIconPickerOpen(v => !v)}
          onMouseEnter={() => setIconHovered(true)}
          onMouseLeave={() => setIconHovered(false)}
          style={{
            width: 34, height: 34, borderRadius: 9, padding: 0, flexShrink: 0,
            border: `1.5px solid ${iconPickerOpen ? WS.accentBd : iconHovered ? WS.border3 : WS.border}`,
            background: WS.surface2, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            boxShadow: iconPickerOpen ? WS.accentGlow : 'none',
            transition: 'all 0.15s',
            position: 'relative',
          }}
        >
          {icon.startsWith('data:')
            ? <img src={icon} style={{ width: 34, height: 34, objectFit: 'cover' }} alt="icon" />
            : <span style={{ fontSize: 18, lineHeight: 1 }}>{icon}</span>
          }
          {(iconHovered || iconPickerOpen) && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8, pointerEvents: 'none',
            }}>
              <Pencil size={11} color="var(--app-on-accent)" />
            </div>
          )}
        </button>

        {iconPickerOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 300,
            background: WS.surface2, border: `1px solid ${WS.border2}`,
            borderRadius: 12, padding: 10,
            boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4, width: 224,
            animation: 'ws-scalein 0.15s cubic-bezier(0.34,1.56,0.64,1)',
          }}>
            {/* Upload */}
            <button
              type="button"
              onClick={() => iconFileRef.current?.click()}
              title="Upload image"
              style={{
                width: 30, height: 30, borderRadius: 7,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: WS.surface3, border: `1.5px dashed ${WS.border3}`,
                cursor: 'pointer', fontSize: 14,
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = WS.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = WS.border3; }}
            >
              📷
            </button>
            {PREDEFINED_ICONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => { setIcon(emoji); setIconPickerOpen(false); }}
                style={{
                  width: 30, height: 30, borderRadius: 7,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: icon === emoji ? WS.accentBg : 'transparent',
                  border: `1.5px solid ${icon === emoji ? WS.accentBd : 'transparent'}`,
                  cursor: 'pointer', fontSize: 16, transition: 'all 0.1s',
                }}
                onMouseEnter={e => { if (icon !== emoji) e.currentTarget.style.background = WS.surface3; }}
                onMouseLeave={e => { if (icon !== emoji) e.currentTarget.style.background = 'transparent'; }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        <input ref={iconFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleIconUpload} />
      </div>

      {/* Agent name + prefill badge */}
      <div className="ws-topbar-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <span className="ws-topbar-title-text" style={{
          fontSize: 14, fontWeight: 600, color: WS.ink,
          letterSpacing: '-0.01em',
        }}>
          {isNew ? 'New Agent' : name || 'Untitled Agent'}
        </span>
        {prefill && (
          <span className="ws-topbar-prefill" style={{
            fontSize: 10.5, fontWeight: 500, flexShrink: 0,
            color: WS.violetText, background: WS.violetBg,
            border: `1px solid ${WS.violetBd}`, padding: '2px 8px', borderRadius: 99,
          }}>
            Template: {prefill.name}
          </span>
        )}
        {/* Status dot */}
        <div style={{
          width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
          background: enabled ? WS.green : WS.ink4,
          boxShadow: enabled ? `0 0 6px ${WS.green}` : 'none',
          marginLeft: 2,
        }} />
      </div>

      {/* Keyboard shortcut hint */}
      <div className="ws-topbar-kbd" style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 9px', borderRadius: 7,
        border: `1px solid ${WS.border}`,
        background: WS.surface2,
        fontSize: 11, color: WS.ink4,
        fontFamily: "'IBM Plex Mono', monospace",
      }}>
        <Cpu size={10} color={WS.ink4} />
        ⌘K
      </div>

      {/* Save button */}
      <button
        className="ws-topbar-save"
        onClick={onSave}
        disabled={saving || saved || !canSave}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 16px', borderRadius: 9,
          background: saved
            ? WS.greenBg
            : WS.gradientAccent,
          color: saved ? WS.greenText : 'var(--lyra-color-surface)',
          border: `1px solid ${saved ? WS.greenBd : 'transparent'}`,
          fontSize: 12.5, fontWeight: 600,
          cursor: saving || saved ? 'default' : 'pointer',
          fontFamily: 'inherit',
          boxShadow: saving || saved ? 'none' : '0 2px 12px undefined',
          transition: 'all 0.18s',
          letterSpacing: '-0.01em',
        }}
        onMouseEnter={e => { if (!saving && !saved) e.currentTarget.style.boxShadow = '0 4px 18px undefined'; }}
        onMouseLeave={e => { if (!saving && !saved) e.currentTarget.style.boxShadow = '0 2px 12px undefined'; }}
      >
        {saving ? <Loader2 size={13} style={{ animation: 'ws-spin 1s linear infinite' }} />
                : saved  ? <Check size={13} />
                         : <Save size={13} />}
        {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Agent'}
      </button>
    </div>
  );
}
